/* eslint-disable react-hooks/exhaustive-deps */
import {
  AutoComplete,
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  List,
  Modal,
  Row,
  Select,
} from 'antd'
import dayjs from 'dayjs'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DeleteOutlined, DownloadOutlined, LoadingOutlined } from '@ant-design/icons'

import { ContentContainer } from '../../../components/StyledComponents'
import {
  useBlocktrainRules,
  useDownload,
  useMaterials,
  useRailcarFiles,
  useRailcarRules,
  useSupplierPurchaseOrders,
  useUpdateRailcar,
} from '../../../hooks'
import {
  BlocktrainDetail,
  BlockTrainStatusEnum,
  RailcarDetail,
  SupplierPurchaseOrder,
} from '../../../types'
import AddRailCarFileModal from './AddRailCarFileModalSupplier'
import parseStringToNumber from '../../../utils/parse-string-to-number'

const { AUTHORIZED, LOADING, WAITING_APPROVAL } = BlockTrainStatusEnum

const allowedEditingStatus = [AUTHORIZED, LOADING, WAITING_APPROVAL]

interface RailcarModalProps {
  onCancel: () => void
  blockTrain: BlocktrainDetail
  railcar: RailcarDetail
  onOk: () => void
  reloadBlocktrain: () => void
}

const RailcarModalViewSupplier: FC<RailcarModalProps> = ({
  onCancel,
  blockTrain,
  railcar,
  reloadBlocktrain,
}) => {
  const { t } = useTranslation()
  const { downloadFile } = useDownload()

  const { purchaseOrders } = useSupplierPurchaseOrders()
  const { materialsOptions } = useMaterials(purchaseOrders)
  const { updateRailcar, updateRailcarIsFetching } = useUpdateRailcar({ id: railcar.id })
  const [form] = Form.useForm()
  const [filteredPurchaseOrders, setFilteredPurchaseOrders] = useState<SupplierPurchaseOrder[]>()
  const [selectedMaterial, setSelectedMaterial] = useState<number>()
  const [addFileModalOpen, setAddFileModalIsOpen] = useState(false)
  const [localRailcarFiles, setLocalRailcarFiles] = useState(railcar.files)
  const { deleteRailcarFile } = useRailcarFiles()
  const [deletingFileIds, setDeletingFileIds] = useState<number[]>([])

  const blocktrainRules = useBlocktrainRules()

  const railCarRules = useRailcarRules()
  const resetForm = (close?: boolean) => {
    form.resetFields()
    if (close) {
      onCancel()
    }
  }

  const handleCancel = () => {
    resetForm(true)
  }

  const onValidationError = (e: any) => {
    console.error(e)
  }

  const handleEditRailcar = async () => {
    await form
      .validateFields()
      .then(values => {
        updateRailcar(values)
        reloadBlocktrain() // actualiza la tabla.
        onCancel()
      })
      .catch(onValidationError)
  }

  const initialValues = useMemo(
    () => ({
      ...railcar,
      deliveryNoteDate: dayjs(railcar?.deliveryNoteDate),
      purchaseOrder: railcar?.purchaseOrder?.id,
      material: railcar?.material?.id,
    }),
    [railcar],
  )

  const disabled = useMemo(() => !allowedEditingStatus.includes(blockTrain?.status), [blockTrain])

  const handleMaterialAndFilteredPurchaseOrders = useCallback(
    (materialId: number) => {
      setSelectedMaterial(materialId)
      const filteredPOs = purchaseOrders?.filter(po => po.material.id === materialId) || []
      setFilteredPurchaseOrders(filteredPOs)
    },
    [purchaseOrders],
  )

  const handleMaterialChange = (materialId: number) => {
    handleMaterialAndFilteredPurchaseOrders(materialId)
    form.setFieldsValue({
      purchaseOrder: undefined,
    })
  }

  const handleAddBlockTrainFile = async (newFile: any) => {
    try {
      setLocalRailcarFiles(prevFiles => {
        const safeFiles = prevFiles || []
        const fileExists = safeFiles.some(file => {
          const existingFileName = file.file?.fileName || file.file.fileName
          const newFileName = newFile.file?.fileName || newFile.fileName

          return file.id === newFile.id || existingFileName === newFileName
        })

        return fileExists ? safeFiles : [...safeFiles, newFile]
      })

      setAddFileModalIsOpen(false)
      reloadBlocktrain()
    } catch (err) {
      console.error('Error uploading file:', err)
    }
  }

  const rulesInputTn = [
    {
      required: true,
      message: t('please_input_value_in_kg'),
    },
    {
      validator: (_: any, value: string) => {
        const parsedValue = parseStringToNumber(value)
        if (parsedValue <= 0) {
          return Promise.reject(t('input_format_required'))
        }
        return Promise.resolve()
      },
    },
  ]

  const formatNumberWithThousandsSeparator = (value: string) => {
    const numericValue = value.replace(/\D/g, '') // Remove non-numeric characters
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.') // Add thousands separator
  }

  const handleNumberChange = (field: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatNumberWithThousandsSeparator(e.target.value)
    form.setFieldsValue({ [field]: formattedValue })
  }

  useEffect(() => {
    if (railcar && purchaseOrders) {
      handleMaterialAndFilteredPurchaseOrders(railcar.material.id)
    }
  }, [railcar, purchaseOrders])

  const handleDeleteFile = (fileId: number): void => {
    // Add file to deleting state
    setDeletingFileIds(prev => [...prev, fileId])

    // Perform delete mutation
    deleteRailcarFile(fileId, {
      onSuccess: () => {
        // Remove file from local list
        const updatedFiles = localRailcarFiles.filter(file => file.id !== fileId)
        setLocalRailcarFiles(updatedFiles)
        reloadBlocktrain()

        // Remove file from deleting state
        setDeletingFileIds(prev => prev.filter(id => id !== fileId))
      },
      onError: () => {
        // Remove file from deleting state on error
        setDeletingFileIds(prev => prev.filter(id => id !== fileId))
      },
    })
  }

  const handleAddFile = (): void => {
    setAddFileModalIsOpen(true)
  }

  return (
    <ContentContainer>
      <Modal
        title={`${t('railcar')} #${blockTrain?.id}-${railcar?.railCarNumber}`}
        open={!!railcar}
        onCancel={handleCancel}
        cancelText={t('cancel')}
        width={900}
        className="entity-modal"
        footer={null}
        onOk={handleEditRailcar}
        okButtonProps={{ disabled: updateRailcarIsFetching }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form form={form} layout="horizontal" initialValues={initialValues}>
              {/* Material  */}
              <Form.Item
                label={t('material')}
                name="material"
                rules={[{ required: true, message: t('please_input_material') }]}
              >
                <Select
                  placeholder={t('select_the_ID_of_the_railcar')}
                  options={materialsOptions}
                  onChange={handleMaterialChange}
                  showSearch
                  disabled={disabled}
                  filterOption={(input, option) => {
                    if (!option) return false
                    return option.label.toLowerCase().includes(input.toLowerCase())
                  }}
                />
              </Form.Item>
              {/* PO Number */}
              <Form.Item
                label={t('po_number')}
                name="purchaseOrder"
                rules={[
                  {
                    required: !!blockTrain.supplier.sapId && !!filteredPurchaseOrders?.length,
                    message: t('please_input_PO_number'),
                  },
                ]}
              >
                <Select
                  placeholder={t('select_po_#')}
                  options={filteredPurchaseOrders?.map(purchaseOrder => ({
                    value: purchaseOrder.id,
                    label: `${purchaseOrder.PurchaseDocumentNumber} - ${purchaseOrder.material.nameShort}`,
                  }))}
                  disabled={disabled || !selectedMaterial || !blockTrain.supplier.sapId}
                  showSearch
                  filterOption={(input, option) => {
                    if (!option) return false
                    return option.label.toLowerCase().includes(input.toLowerCase())
                  }}
                />
              </Form.Item>
              <Form.Item
                label={t('railcar_ID')}
                name="railCarId"
                rules={[
                  ...railCarRules.railCarId,
                  {
                    validator: async (_, value) => {
                      if (railcar.railCarId === value) {
                        return Promise.resolve()
                      }

                      const existingRailcar = blockTrain.railCars.some(
                        railCar => railCar.railCarId === value,
                      )

                      if (existingRailcar) {
                        return Promise.reject(new Error(t('railcar_id_already_in_use')))
                      }

                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <Input
                  className={disabled ? 'border-unset' : ''}
                  placeholder={t('select_the_ID_of_the_railcar')}
                  readOnly={disabled}
                />
              </Form.Item>
              {/* Delivery Note */}
              <Form.Item
                label={t('delivery_note')}
                name="deliveryNote"
                rules={blocktrainRules.deliveryNote}
              >
                <AutoComplete
                  showSearch
                  placeholder={t('select_delivery_note')}
                  filterOption
                  onChange={value => form.setFieldsValue({ deliveryNote: value })}
                  onBlur={() => {
                    const currentValue = form.getFieldValue('deliveryNote')
                    if (currentValue && typeof currentValue === 'number') {
                      form.setFieldsValue({ deliveryNote: currentValue })
                    }
                  }}
                  options={[
                    ...new Set(blockTrain.railCars.map(({ deliveryNote }) => deliveryNote)),
                  ].map(value => ({ value }))}
                  disabled={disabled}
                />
              </Form.Item>
              {/* Delivery Note Date */}
              <Form.Item
                label={t('delivery_note_date')}
                name="deliveryNoteDate"
                rules={[{ required: true, message: t('please_input_delivery_note_date') }]}
                labelCol={{ span: t('delivery_note_date').length > 24 ? 24 : undefined }}
              >
                <DatePicker placeholder={t('select_date')} disabled={disabled} />
              </Form.Item>
              {/* Gross KG */}
              <Form.Item
                label={t('gross_kg')}
                name="grossTn"
                rules={rulesInputTn}
                validateTrigger={['']}
              >
                <Input
                  readOnly={disabled}
                  className={disabled ? 'border-unset' : ''}
                  placeholder={t('input_gross_kg')}
                  onChange={e => handleNumberChange('grossTn', e)}
                />
              </Form.Item>
              {/* Tare KG */}
              <Form.Item
                label={t('tare_kg')}
                name="tareTn"
                rules={rulesInputTn}
                validateTrigger={['']}
              >
                <Input
                  readOnly={disabled}
                  className={disabled ? 'border-unset' : ''}
                  placeholder={t('input_tare_kg')}
                  onChange={e => handleNumberChange('tareTn', e)}
                />
              </Form.Item>
              {/* Net KG */}
              <Form.Item
                label={t('net_kg')}
                name="netTn"
                rules={rulesInputTn}
                validateTrigger={['']}
              >
                <Input
                  readOnly={disabled}
                  className={disabled ? 'border-unset' : ''}
                  placeholder={t('input_net_kg')}
                  onChange={e => handleNumberChange('netTn', e)}
                />
              </Form.Item>
              {/* Buttons Cancel and Save */}
              <div className="modal-footer" style={{ textAlign: 'right', marginTop: '16px' }}>
                <Button onClick={handleCancel} style={{ marginRight: '8px' }}>
                  {t('cancel')}
                </Button>
                <Button onClick={handleEditRailcar} type="primary" disabled={disabled}>
                  {t('save')}
                </Button>
              </div>
            </Form>
          </Col>
          <div>
            <Divider
              type="vertical"
              style={{ height: '100%', width: '1px', backgroundColor: 'grey' }}
            />
          </div>
          {/* //TODO: files */}
          <Col span={11}>
            <div>
              <Button
                onClick={handleAddFile}
                type="primary"
                style={{ margin: '0 0.75rem', marginBottom: '0.75rem' }}
                disabled={disabled}
              >
                {t('add_file')}
              </Button>

              <List
                style={{ maxHeight: '400px', overflowY: 'auto' }}
                dataSource={localRailcarFiles}
                renderItem={item => (
                  <List.Item
                    style={{ padding: '8px 16px' }}
                    actions={[
                      <Button
                        key="download"
                        icon={<DownloadOutlined />}
                        size="small"
                        onClick={() =>
                          downloadFile({ key: item.file.ticketFile, name: item.file.fileName })
                        }
                      />,
                      <Button
                        key="delete"
                        icon={
                          deletingFileIds.includes(item.id) ? (
                            <LoadingOutlined spin />
                          ) : (
                            <DeleteOutlined />
                          )
                        }
                        size="small"
                        loading={deletingFileIds.includes(item.id)}
                        onClick={() => handleDeleteFile(item.id)}
                        disabled={deletingFileIds.includes(item.id) || disabled}
                      />,
                    ]}
                  >
                    <List.Item.Meta
                      title={<span style={{ fontSize: '14px' }}>{item.file.fileName}</span>}
                      description={
                        <span style={{ fontSize: '12px' }}>{t(item.type?.name || '')} </span>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
          </Col>
        </Row>
      </Modal>

      <AddRailCarFileModal
        visible={addFileModalOpen}
        onCancel={() => setAddFileModalIsOpen(false)}
        onCreate={handleAddBlockTrainFile}
        railCar={railcar}
      />
    </ContentContainer>
  )
}

export default RailcarModalViewSupplier
